import React, { useState, useEffect, useMemo } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { FormInput } from '../../components/Forms/FormInput';
import { ActionButton } from '../../components/Forms/ActionButton';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCreateQuiz, useGetQuizById, useGetUserQuizSubmissions, useUpdateQuiz } from '../../queries/quiz';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TanStackTable from '../../components/TanStackTable';
import { createColumnHelper } from '@tanstack/react-table';
import { useGetAllUsers } from '../../queries/user';
import ActiveStatus from '../../components/Common/ActiveStatus';
import { useGetAppDisplayConfig } from '../../queries/homepageOrganizer';

const CreateQuiz = () => {

    const navigate = useNavigate();
    const { state } = useLocation();
    const quizId = state?.quizId;

    const { data, isLoading } = useGetQuizById(quizId);
    // const { data: users = [], isLoading: isUserListLoading } = useGetAllUsers(toast);
    const { data: users = [], isLoading: isUserListLoading } = useGetUserQuizSubmissions(quizId);
    const { data: appDisplayConfig, isPending } = useGetAppDisplayConfig();

    // This state will hold the quiz attempt data (enrolled users)
    const [quizEnrollments, setQuizEnrollments] = useState([]);
    const [active, setActive] = useState(false);

    const { mutateAsync: createQuiz, isPending: isSubmitting } = useCreateQuiz();
    const { mutateAsync: editQuiz, isPending: isUpdating } = useUpdateQuiz(quizId);

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        setValue,
        watch,
        reset,
        getValues,
    } = useForm({
        defaultValues: {
            quizName: "",
            pointsOnCorrectAnswer: appDisplayConfig?.quizPoints || 10,
            questions: [
                {
                    questionText: "",
                    options: [
                        { text: "", isCorrect: true },
                        { text: "", isCorrect: false },
                        { text: "", isCorrect: false },
                        { text: "", isCorrect: false }
                    ]
                }
            ]
        }
    });

    // Use field array to handle dynamic questions
    const { fields, append, remove } = useFieldArray({
        control,
        name: "questions"
    });

    // Watch for changes in questions array and points per question to calculate total points
    const watchedQuestions = watch("questions");
    const watchedPointsPerQuestion = watch("pointsOnCorrectAnswer");

    // Calculate total points automatically
    const totalPoints = useMemo(() => {
        const numberOfQuestions = watchedQuestions?.length || 0;
        const pointsPerQuestion = watchedPointsPerQuestion || 0;
        return numberOfQuestions * pointsPerQuestion;
    }, [watchedQuestions, watchedPointsPerQuestion]);

    const onSubmit = async (data) => {
        try {
            // Format questions for API
            const formattedQuestions = data.questions.map(question => ({
                text: question.questionText,
                answers: question.options.map(option => ({
                    text: option.text,
                    isCorrect: option.isCorrect
                }))
            }));

            console.log({
                title: data.quizName,
                pointsPerCorrectAnswer: data.pointsOnCorrectAnswer,
                questions: formattedQuestions
            });

            // Here you would add your API call to create/edit the quiz
            // Example:
            toast.loading('Submitting quiz...');
            if (quizId) {
                await editQuiz({
                    id: quizId,
                    title: data.quizName,
                    pointsPerCorrectAnswer: data.pointsOnCorrectAnswer,
                    questions: formattedQuestions
                }, {
                    onSuccess: (res) => {
                        toast.dismiss();
                        toast.success("Quiz updated successfully");
                        console.log("Quiz updated successfully ", res);
                        setTimeout(() => {
                            navigate('/quiz/quiz-list');
                        }, 2000);
                    },
                    onError: (error) => {
                        console.error("Error updating quiz:", error);
                        toast.dismiss();
                        toast.error("Failed to update quiz: " + (error.message || "Unknown error"));
                    }
                });
            } else {
                await createQuiz({
                    title: data.quizName,
                    pointsPerCorrectAnswer: data.pointsOnCorrectAnswer,
                    questions: formattedQuestions
                }, {
                    onSuccess: (res) => {
                        toast.dismiss();
                        toast.success("Quiz created successfully");
                        console.log("Quiz created successfully ", res);
                        setTimeout(() => {
                            navigate('/quiz/quiz-list');
                        }, 2000);
                    },
                    onError: (error) => {
                        console.error("Error creating quiz:", error);
                        toast.dismiss();
                        toast.error("Failed to create quiz: " + (error.message || "Unknown error"));
                    }
                });
            }
        } catch (error) {
            console.error("Error submitting quiz:", error);
            toast.dismiss();
            toast.error("Failed to submit quiz: " + (error.message || "Unknown error"));
        }
    };

    // Fetch quiz enrollment data for edit mode
    useEffect(() => {
        // This is a mock implementation - in a real scenario, you would call an API
        // Example API call would be:
        // if (quizId) {
        //     const fetchQuizEnrollments = async () => {
        //         try {
        //             const response = await api.get(`/quiz-enrollments?quizId=${quizId}`);
        //             setQuizEnrollments(response.data.enrollments);
        //         } catch (error) {
        //             console.error("Error fetching quiz enrollments:", error);
        //             toast.error("Failed to load quiz enrollments");
        //         }
        //     };
        //     fetchQuizEnrollments();
        // }

        // For now, using mock data if in edit mode
        // if (quizId) {
        //     // Mock data for demonstration
        //     setQuizEnrollments([
        //         {
        //             id: '1',
        //             userId: 'user1',
        //             username: 'John Doe',
        //             profilepic: 'https://via.placeholder.com/40',
        //             selectedOption: 'Option A',
        //             isCorrect: true,
        //             points: 10,
        //             attemptedAt: new Date().toISOString()
        //         },
        //         {
        //             id: '2',
        //             userId: 'user2',
        //             username: 'Jane Smith',
        //             profilepic: 'https://via.placeholder.com/40',
        //             selectedOption: 'Option B',
        //             isCorrect: false,
        //             points: 0,
        //             attemptedAt: new Date().toISOString()
        //         }
        //     ]);
        // }
    }, [quizId]);

    useEffect(() => {
        // Fetch quiz data if editing
        if(appDisplayConfig){
            setValue('pointsOnCorrectAnswer', appDisplayConfig.quizPoints || 10);
        }
        if (data) {
            // Set quiz data from the API response
            setValue('quizName', data.title);
            setValue('pointsOnCorrectAnswer', data.pointsPerCorrectAnswer);

            // Reset questions array
            const formattedQuestions = data.questionIds.map(question => {
                return {
                    questionText: question.text,
                    options: question.optionIds.map(option => ({
                        text: option.text,
                        isCorrect: option.isCorrect
                    }))
                };
            });
            setActive(data.active);
            // Set the questions array
            setValue('questions', formattedQuestions);
        }
    }, [data, setValue, appDisplayConfig]);

    // Table column definition for quiz enrollments
    const columnHelper = createColumnHelper();

    const columns = useMemo(() => [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
            size: 80,
        }),
        columnHelper.accessor('name', {
            header: 'User',
            cell: info => {
                const name = info.getValue();
                return (
                    <div className="line-clamp-1 flex items-center gap-2">
                        {name?.firstName && (
                            `${name.firstName}${name.lastName ? ` ${name.lastName}` : ''}`
                        ) || 'N/A'}
                    </div>
                );
            },
            size: 150,
        }),
        columnHelper.accessor('totalQuestions', {
            header: 'Total Questions',
            align: 'center',
            cell: info => info.getValue() || 0,
            size: 120,
        }),
        columnHelper.accessor('correctAnswers', {
            header: 'Correct Answers',
            align: 'center',
            cell: info => {
                const correctAnswers = info.getValue() || 0;
                const totalQuestions = info.row.original.totalQuestions || 0;
                
                return (
                    <div className="flex justify-center items-center px-2 py-1 rounded-md">
                        {correctAnswers}/{totalQuestions}
                    </div>
                );
            },
            size: 120,
        }),
        columnHelper.accessor('totalPoints', {
            header: 'Points Scored',
            align: 'center',
            cell: info => {
                const scoredPoints = info.getValue() || 0;
                const totalPossiblePoints = info.row.original.totalQuizPoints || 0;
                
                return (
                    <div className="text-center font-semibold">
                        {scoredPoints}/{totalPossiblePoints}
                    </div>
                );
            },
            size: 150,
        }),
    ], []);

    if (isLoading || isUserListLoading || isPending) {
        return <div className="flex items-center justify-center h-screen">Loading...</div>;
    }

    return (
        <div className="flex flex-col w-full font-semibold text-base">
            <div className='flex items-center justify-between'>
                <h1 className="text-xl font-bold mb-4">
                    {quizId ? 'Edit Quiz' : 'Create Quiz'}
                </h1>
                <ActiveStatus
                    active={active}
                    setActive={setActive}
                    isCreateForm={true}
                />
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col w-full font-semibold">
                <section className="flex flex-col justify-center px-5 pt-2 py-9 gap-3 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)]">
                    <p className="text-base font-bold mt-3">Quiz Information</p>

                    <div className="flex gap-3">
                        <div className="flex flex-col w-full gap-4">
                            {/* Quiz Name */}
                            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                <div className="flex justify-between items-center">
                                    <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Name Of The Quiz</label>
                                </div>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Enter quiz name"
                                        {...register("quizName", { required: "Quiz name is required" })}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.quizName ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.quizName && <p className="text-red text-sm">{errors.quizName.message}</p>}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Dynamic Questions */}
                {fields.map((field, questionIndex) => (
                    <section key={field.id} className="flex flex-col justify-center px-5 pt-2 py-9 gap-3 w-full bg-navBtn rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] mt-5">
                        <div className="flex justify-between items-center mt-2">
                            <p className="text-base font-bold">Question {questionIndex + 1}</p>
                            {fields.length > 1 && (
                                <ActionButton
                                    variant={'white'}
                                    text={'Remove Question'}
                                    handleSubmit={() => remove(questionIndex)}
                                />
                            )}
                        </div>

                        {/* Question Text */}
                        <div className="flex flex-col flex-1 w-full mt-1.5 mb-4">
                            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Question Text</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    {...register(`questions.${questionIndex}.questionText`, { required: "Question text is required" })}
                                    placeholder="Enter your question"
                                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.questions?.[questionIndex]?.questionText ? "border-red-500" : ""}`}
                                />
                            </div>
                            {errors.questions?.[questionIndex]?.questionText && (
                                <p className="text-red-500 text-sm">{errors.questions[questionIndex].questionText.message}</p>
                            )}
                        </div>

                        <p className="text-base font-semibold mt-3">Answer Options</p>

                        {/* Options - First Row */}
                        <div className='flex items-center justify-between gap-3'>
                            {/* Option 1 */}
                            <div className="flex items-center gap-4 mb-6 basis-1/2">
                                <div className="flex-1">
                                    <div className='flex items-center mb-2 justify-between'>
                                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Option 1</label>
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id={`correctAnswer-${questionIndex}-0`}
                                                {...register(`questions.${questionIndex}.options.0.isCorrect`)}
                                                className="h-5 mr-2 accent-green-600"
                                                onChange={(e) => {
                                                    // If checked, uncheck other options for this question
                                                    if (e.target.checked) {
                                                        const currentValues = getValues();
                                                        const updatedOptions = currentValues.questions[questionIndex].options.map((opt, idx) => ({
                                                            ...opt,
                                                            isCorrect: idx === 0
                                                        }));
                                                        updatedOptions.forEach((opt, idx) => {
                                                            setValue(`questions.${questionIndex}.options.${idx}.isCorrect`, idx === 0);
                                                        });
                                                    }
                                                }}
                                            />
                                            <label htmlFor={`correctAnswer-${questionIndex}-0`} className="w-max font-semibold">
                                                Correct Answer
                                            </label>
                                        </div>
                                    </div>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            {...register(`questions.${questionIndex}.options.0.text`, { required: "Option 1 is required" })}
                                            placeholder="Enter option 1"
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.questions?.[questionIndex]?.options?.[0]?.text ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.questions?.[questionIndex]?.options?.[0]?.text && (
                                        <p className="text-red-500 text-sm">{errors.questions[questionIndex].options[0].text.message}</p>
                                    )}
                                </div>
                            </div>

                            {/* Option 2 */}
                            <div className="flex items-center gap-4 mb-6 basis-1/2">
                                <div className="flex-1">
                                    <div className='flex items-center mb-2 justify-between'>
                                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Option 2</label>
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id={`correctAnswer-${questionIndex}-1`}
                                                {...register(`questions.${questionIndex}.options.1.isCorrect`)}
                                                className="h-5 mr-2 accent-green-600"
                                                onChange={(e) => {
                                                    // If checked, uncheck other options for this question
                                                    if (e.target.checked) {
                                                        const currentValues = getValues();
                                                        const updatedOptions = currentValues.questions[questionIndex].options.map((opt, idx) => ({
                                                            ...opt,
                                                            isCorrect: idx === 1
                                                        }));
                                                        updatedOptions.forEach((opt, idx) => {
                                                            setValue(`questions.${questionIndex}.options.${idx}.isCorrect`, idx === 1);
                                                        });
                                                    }
                                                }}
                                            />
                                            <label htmlFor={`correctAnswer-${questionIndex}-1`} className="w-max font-semibold">
                                                Correct Answer
                                            </label>
                                        </div>
                                    </div>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            {...register(`questions.${questionIndex}.options.1.text`, { required: "Option 2 is required" })}
                                            placeholder="Enter option 2"
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.questions?.[questionIndex]?.options?.[1]?.text ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.questions?.[questionIndex]?.options?.[1]?.text && (
                                        <p className="text-red-500 text-sm">{errors.questions[questionIndex].options[1].text.message}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Options - Second Row */}
                        <div className='flex items-center justify-between gap-3'>
                            {/* Option 3 */}
                            <div className="flex items-center gap-4 mb-6 basis-1/2">
                                <div className="flex-1">
                                    <div className='flex items-center mb-2 justify-between'>
                                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Option 3</label>
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id={`correctAnswer-${questionIndex}-2`}
                                                {...register(`questions.${questionIndex}.options.2.isCorrect`)}
                                                className="h-5 mr-2 accent-green-600"
                                                onChange={(e) => {
                                                    // If checked, uncheck other options for this question
                                                    if (e.target.checked) {
                                                        const currentValues = getValues();
                                                        const updatedOptions = currentValues.questions[questionIndex].options.map((opt, idx) => ({
                                                            ...opt,
                                                            isCorrect: idx === 2
                                                        }));
                                                        updatedOptions.forEach((opt, idx) => {
                                                            setValue(`questions.${questionIndex}.options.${idx}.isCorrect`, idx === 2);
                                                        });
                                                    }
                                                }}
                                            />
                                            <label htmlFor={`correctAnswer-${questionIndex}-2`} className="w-max font-semibold">
                                                Correct Answer
                                            </label>
                                        </div>
                                    </div>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            {...register(`questions.${questionIndex}.options.2.text`, { required: "Option 3 is required" })}
                                            placeholder="Enter option 3"
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.questions?.[questionIndex]?.options?.[2]?.text ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.questions?.[questionIndex]?.options?.[2]?.text && (
                                        <p className="text-red-500 text-sm">{errors.questions[questionIndex].options[2].text.message}</p>
                                    )}
                                </div>
                            </div>

                            {/* Option 4 */}
                            <div className="flex items-center gap-4 mb-6 basis-1/2">
                                <div className="flex-1">
                                    <div className='flex items-center mb-2 justify-between'>
                                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Option 4</label>
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id={`correctAnswer-${questionIndex}-3`}
                                                {...register(`questions.${questionIndex}.options.3.isCorrect`)}
                                                className="h-5 mr-2 accent-green-600"
                                                onChange={(e) => {
                                                    // If checked, uncheck other options for this question
                                                    if (e.target.checked) {
                                                        const currentValues = getValues();
                                                        const updatedOptions = currentValues.questions[questionIndex].options.map((opt, idx) => ({
                                                            ...opt,
                                                            isCorrect: idx === 3
                                                        }));
                                                        updatedOptions.forEach((opt, idx) => {
                                                            setValue(`questions.${questionIndex}.options.${idx}.isCorrect`, idx === 3);
                                                        });
                                                    }
                                                }}
                                            />
                                            <label htmlFor={`correctAnswer-${questionIndex}-3`} className="w-max font-semibold">
                                                Correct Answer
                                            </label>
                                        </div>
                                    </div>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            {...register(`questions.${questionIndex}.options.3.text`, { required: "Option 4 is required" })}
                                            placeholder="Enter option 4"
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.questions?.[questionIndex]?.options?.[3]?.text ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.questions?.[questionIndex]?.options?.[3]?.text && (
                                        <p className="text-red-500 text-sm">{errors.questions[questionIndex].options[3].text.message}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>
                ))}

                <div className='flex justify-end mt-4'>
                    <ActionButton
                        variant={'reset'}
                        text={"+ Add Question"}
                        handleSubmit={() => append({
                            questionText: "",
                            options: [
                                { text: "", isCorrect: true },
                                { text: "", isCorrect: false },
                                { text: "", isCorrect: false },
                                { text: "", isCorrect: false }
                            ]
                        })}
                    />
                </div>

                {/* Points on correct answer */}
                <section className="flex flex-col justify-center px-5 pt-2 py-9 gap-3 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] mt-5">
                    <p className="text-base font-bold mt-3">Quiz Points</p>

                    <div className='flex gap-3'>
                        <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full">
                            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Points Per Question</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="number"
                                    placeholder="Enter points"
                                    {...register("pointsOnCorrectAnswer", {
                                        required: "Points are required",
                                        min: {
                                            value: 1,
                                            message: "Points must be at least 1"
                                        },
                                        valueAsNumber: true
                                    })}
                                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.pointsOnCorrectAnswer ? "border-red-500" : ""}`}
                                />
                            </div>
                            {errors.pointsOnCorrectAnswer && <p className="text-red text-sm">{errors.pointsOnCorrectAnswer.message}</p>}
                        </div>
                        <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full">
                            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Total Points</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-gray-50 rounded-md border border-solid border-navBtn text-gray-700">
                                <div className="flex-1 shrink self-stretch my-auto basis-0 px-4 py-2.5 rounded-md text-base font-semibold">
                                    {totalPoints} points
                                </div>
                            </div>
                            <p className="text-gray-500 text-xs mt-1">Calculated as: {watchedQuestions?.length || 0} questions × {watchedPointsPerQuestion || 0} points</p>
                        </div>
                    </div>
                </section>

                {/* Form Actions */}
                <div className="mt-10 flex gap-5 items-center justify-between">
                    <ActionButton
                        variant="reset"
                        text="Reset"
                        handleSubmit={() => {
                            reset();
                        }}
                    />
                    <div className="flex items-center gap-3">
                        <ActionButton
                            variant="cancel"
                            text="Cancel"
                            handleSubmit={() => navigate('/quiz/quiz-list')}
                        />
                        <ActionButton
                            variant="submit"
                            text={quizId ? "Update Quiz" : "Create Quiz"}
                            isSubmit={true}
                            disabled={isSubmitting}
                        />
                    </div>
                </div>

            </form>
            {/* User enrollments table - Only show in edit mode */}
            {quizId && (
                <section className="flex flex-col justify-center px-5 pt-2 py-9 gap-3 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] mt-5">
                    <p className="text-base font-bold mt-3">User Quiz Enrollments</p>

                    <div className="mt-4">
                        <TanStackTable
                            data={users}
                            columns={columns}
                            enablePagination={true}
                            defaultPageSize={10}
                        />
                    </div>
                </section>
            )}
            <ToastContainer
                position="top-right"
            />
        </div>
    );
};

export default CreateQuiz;